import { Badge, Button, Card, LoadingContainer, Modal } from '@/components/ui';
import { useAudiosEliminadosPorReasignacion } from '@/hooks/useSysadmin';
import { formatHorasDecimales } from '@/utils/formatDuration';
import { AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';
import { useState } from 'react';

function formatFecha(fecha: string) {
  try {
    return new Date(fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * Audios que quedaron ocultos (estado ELIMINADO) al rechazarse dentro de una asesoría reasignada
 * a otro asesor (revisarAudios) — hoy solo se ocultan de las vistas activas, pero el archivo real
 * en Google Drive y la fila en la base de datos siguen intactos. Este panel permite revisarlos
 * antes de un borrado físico real (papelera de Drive + fila de BD), que además corrige las horas
 * de la asesoría que quedaron infladas por ellos.
 */
export function AudiosEliminadosReasignacionPanel() {
  const { audios, isLoading, error, isDeleting, eliminarSeleccionados } = useAudiosEliminadosPorReasignacion();
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [resultado, setResultado] = useState<{ total: number; fallidos: number } | null>(null);

  const toggleUno = (audioId: number) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(audioId)) next.delete(audioId);
      else next.add(audioId);
      return next;
    });
  };

  const toggleTodos = () => {
    setSeleccionados((prev) => (prev.size === audios.length ? new Set() : new Set(audios.map((a) => a.audioId))));
  };

  const handleConfirmarBorrado = async () => {
    const res = await eliminarSeleccionados(Array.from(seleccionados));
    setResultado(res);
    setSeleccionados(new Set());
    setShowConfirm(false);
  };

  if (isLoading && audios.length === 0) return <LoadingContainer text="Cargando audios eliminados por reasignación..." />;

  if (error) {
    return (
      <Card className="p-4 bg-red-50 border-red-100 text-red-700">
        <p className="text-sm font-medium">Error al cargar: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50/80 border border-amber-200 rounded-xl flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-amber-900 text-sm">
            {audios.length} audio{audios.length === 1 ? '' : 's'} oculto{audios.length === 1 ? '' : 's'} por cambio de asesor
          </h3>
          <p className="text-xs text-amber-800 mt-0.5">
            Se ocultaron al rechazarse dentro de una asesoría cuyo asesor cambió (nadie pudo corregirlos). El archivo real en Drive y la
            fila en la base de datos siguen intactos — revisa antes de eliminarlos de verdad.
          </p>
        </div>
      </div>

      {resultado && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {resultado.total - resultado.fallidos} de {resultado.total} audios eliminados correctamente
          {resultado.fallidos > 0 && ` (${resultado.fallidos} fallaron, revisa logs)`}.
        </div>
      )}

      {audios.length === 0 ? (
        <Card className="p-8 text-center text-gray-500 text-sm">No hay audios ocultos por reasignación pendientes de revisión.</Card>
      ) : (
        <Card className="overflow-hidden border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
              <input type="checkbox" checked={seleccionados.size > 0 && seleccionados.size === audios.length} onChange={toggleTodos} />
              Seleccionar todos
            </label>
            {seleccionados.size > 0 && (
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 text-xs py-1.5 px-3"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar {seleccionados.size} seleccionado{seleccionados.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="w-8 px-4 py-3"></th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Asesoría</th>
                  <th className="px-4 py-3">Duración</th>
                  <th className="px-4 py-3">Fecha grabación</th>
                  <th className="px-4 py-3">Observación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audios.map((a) => (
                  <tr key={a.audioId} className={seleccionados.has(a.audioId) ? 'bg-amber-50/40' : ''}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={seleccionados.has(a.audioId)} onChange={() => toggleUno(a.audioId)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.nombreEmpresa}</div>
                      <div className="text-xs text-gray-400 font-mono">{a.nitEmpresa}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                        {a.ordenAsesoria != null ? `Asesoría ${a.ordenAsesoria}` : `#${a.asesoriaId}`}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatHorasDecimales(a.duracionSeg / 3600)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatFecha(a.fechaGrabacion)}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-xs text-gray-400">{a.observacion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showConfirm && (
        <Modal isOpen={true} onClose={() => setShowConfirm(false)} title="Confirmar borrado físico">
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm">
                Vas a mandar {seleccionados.size} archivo{seleccionados.size === 1 ? '' : 's'} a la papelera de Google Drive (reversible) y
                borrar su registro de la base de datos (esto sí es permanente). Las horas de cada asesoría se van a corregir
                automáticamente.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setShowConfirm(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700">
                Cancelar
              </Button>
              <Button onClick={handleConfirmarBorrado} disabled={isDeleting} className="bg-red-600 hover:bg-red-700 text-white">
                {isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
